import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import {
  validateDecimalNumer,
} from '../../validation/ValidationInput';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from '../../services/fieldLimits';
import FieldCounter from '../../components/FieldCounter';
import BackIcon from '../../images/BackIcon';
import {
  createAppVersion,
  getAppVersion,
  updateAppVersion,
} from '../../services/appVersion.service';
import { useResponsive } from '../../hook/useResponsive';
import UploadIcon from '../../images/UploadIcon';

interface AppVersionFormValues {
  platform: string;
  appName: string;
  releaseName: string;
  releaseNumber: string;
  minimumAcceptedVersion: string;
  rolloutScope: string;
  isActive: boolean;
}

const MAX_UPLOAD_FILE_SIZE = 500 * 1024 * 1024; // 500MB

// The app name is what a till sends when it asks for updates, so it is the
// platform: Windows tills take a launcher zip, Android tablets a signed APK.
const PLATFORMS = [
  { value: 'windows', appName: 'finex-pos', label: 'Windows', extension: '.zip', accept: '.zip,application/zip' },
  { value: 'android', appName: 'finex-pos-android', label: 'Android', extension: '.apk', accept: '.apk,application/vnd.android.package-archive' },
];

const platformOfAppName = (appName: string) =>
  PLATFORMS.find(p => p.appName === String(appName ?? '').trim());

const normalizeDetail = (raw: any) => {
  const d = raw?.data ?? raw;
  const packageUrl =
    d?.packageUrl ??
    d?.package_url ??
    d?.fileUrl ??
    d?.file_url ??
    d?.url ??
    '';
  const packageFileName = (() => {
    if (!packageUrl || typeof packageUrl !== 'string') return '';
    try {
      const withoutQuery = packageUrl.split('?')[0];
      const lastPart = withoutQuery.substring(withoutQuery.lastIndexOf('/') + 1);
      return decodeURIComponent(lastPart);
    } catch {
      return '';
    }
  })();

  return {
    appName: d?.appName ?? d?.app_name ?? '',
    releaseName: d?.releaseName ?? d?.release_name ?? '',
    releaseNumber: d?.releaseNumber ?? d?.release_number ?? '',
    minimumAcceptedVersion:
      d?.minimumAcceptedVersion ?? d?.minimum_accepted_version ?? '',
    rolloutScope: String(d?.rolloutScope ?? d?.rollout_scope ?? ''),
    existingFileName:
      d?.fileName ?? d?.file_name ?? d?.versionFileName ?? packageFileName ?? '',
    existingPackageUrl: packageUrl,
    isActive: Boolean(d?.isActive ?? d?.is_active ?? true),
  };
};

const CreateAppVersion = () => {
  const [formValues, setFormValues] = useState<AppVersionFormValues>({
    platform: '',
    appName: '',
    releaseName: '',
    releaseNumber: '',
    minimumAcceptedVersion: '',
    rolloutScope: '',
    isActive: true,
  });
  const { isMobile } = useResponsive();
  const preLanguage = localStorage.getItem('language');
  const [language, setLanguage] = useState<any>(null);
  const limits = useFieldLimits(inventoryFormLimits('app-version'));
  const [errors, setErrors] = useState<any>({});
  const path = window.location.pathname;
  const id: string = path.split('/').pop() || '';
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [existingFileLabel, setExistingFileLabel] = useState('');
  const [existingPackageUrl, setExistingPackageUrl] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    field: keyof AppVersionFormValues,
    value: string | boolean
  ) => {
    setFormValues(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const normalizeMinAcceptedVersion = (raw: string): string => {
    // Allow digits and dots as-is, including multiple dots.
    // We will still sanitize any non-digit/non-dot chars.
    return raw.replace(/[^\d.]/g, '');
  };

  const isValidMinAcceptedVersionChars = (raw: string) => {
    // Allow digits and dots only.
    return /^[0-9.]*$/.test(raw);
  };

  const validateMinAcceptedVersionKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const allowedControlKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];
    if (allowedControlKeys.includes(e.key)) return;

    const { key } = e;
    // Allow common shortcuts (copy/paste/select)
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(key.toLowerCase())) {
      return;
    }

    if (/^\d$/.test(key)) return;

    if (key === '.') {
      return;
    }

    // Disallow spaces/letters/special chars.
    e.preventDefault();
  };

  const selectedPlatform = PLATFORMS.find(p => p.value === formValues.platform);
  // Until a platform is chosen either package type may be picked; the submit check settles it.
  const allowedExtensions = selectedPlatform ? [selectedPlatform.extension] : PLATFORMS.map(p => p.extension);
  const matchesPlatform = (f: File) => allowedExtensions.some(ext => f.name.toLowerCase().endsWith(ext));
  const fileTypeError = () => {
    if (selectedPlatform?.value === 'android') {
      return language?.['Please upload an .apk file'] ?? 'Please upload an .apk file';
    }
    if (selectedPlatform?.value === 'windows') {
      return language?.['Please upload a .zip file'] ?? 'Please upload a .zip file';
    }
    return language?.['Please upload a .zip or .apk file'] ?? 'Please upload a .zip or .apk file';
  };

  const handlePlatformChange = (value: string) => {
    const platform = PLATFORMS.find(p => p.value === value);
    setFormValues(prev => ({ ...prev, platform: value, appName: platform?.appName ?? prev.appName }));
    setErrors((prev: any) => {
      const next = { ...prev };
      delete next.platform;
      delete next.appName;
      return next;
    });
    // A file picked for the other platform no longer fits.
    if (file && platform && !file.name.toLowerCase().endsWith(platform.extension)) {
      handleRemoveFile();
    }
  };

  const setPackageFile = (f: File | null) => {
    if (!f) {
      setFile(null);
      setFileName('');
      return;
    }
    if (!matchesPlatform(f)) {
      setErrors((prev: any) => ({ ...prev, file: fileTypeError() }));
      return;
    }
    if (f.size > MAX_UPLOAD_FILE_SIZE) {
      setErrors((prev: any) => ({
        ...prev,
        file: language?.['File size must be less than or equal to 500MB']
          ?? 'File size must be less than or equal to 500MB',
      }));
      return;
    }
    setErrors((prev: any) => {
      const next = { ...prev };
      delete next.file;
      return next;
    });
    setFile(f);
    setFileName(f.name);
  };

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const fetchData = async (versionId: string) => {
    webLoadingTrue();
    try {
      const response: any = await getAppVersion(versionId);
      const data = normalizeDetail(response.data);
      setFormValues({
        platform: platformOfAppName(data.appName)?.value ?? '',
        appName: data.appName,
        releaseName: data.releaseName,
        releaseNumber: data.releaseNumber,
        minimumAcceptedVersion: data.minimumAcceptedVersion,
        rolloutScope: data.rolloutScope,
        isActive: data.isActive,
      });
      if (data.existingFileName) {
        setExistingFileLabel(String(data.existingFileName));
      }
      if (data.existingPackageUrl) {
        setExistingPackageUrl(String(data.existingPackageUrl));
      }
    } catch (error) {
      console.error('Error loading app version:', error);
    } finally {
      webLoadingFalse();
    }
  };

  useEffect(() => {
    if (id !== 'new') {
      fetchData(id);
    }
    const handleNewToken = () => {
      if (id !== 'new') {
        fetchData(id);
      }
    };
    document.addEventListener('newToken', handleNewToken);
    return () => {
      document.removeEventListener('newToken', handleNewToken);
    };
  }, [path]);

  const buildFormData = () => {
    const formData = new FormData();
    formData.append('appName', formValues.appName.trim());
    formData.append('releaseName', formValues.releaseName.trim());
    formData.append('releaseNumber', formValues.releaseNumber.trim());
    formData.append(
      'minimumAcceptedVersion',
      formValues.minimumAcceptedVersion.trim()
    );
    formData.append('rolloutScope', String(Number(formValues.rolloutScope)));
    formData.append('isActive', String(formValues.isActive));
    if (file) {
      formData.append('file_upload', file);
    }
    return formData;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: any = validateAgainstLimits(formValues as any, limits, language, [
      'appName',
      'releaseName',
      'releaseNumber',
      'minimumAcceptedVersion',
    ]);
    if (!formValues.platform) {
      newErrors.platform = language?.['Platform is required'] ?? 'Platform is required';
    }
    if (!formValues.appName.trim()) newErrors.appName = 'App name is required';
    if (file && !matchesPlatform(file)) newErrors.file = fileTypeError();
    if (!formValues.releaseName.trim()) newErrors.releaseName = 'Release name is required';
    if (!formValues.releaseNumber.trim()) newErrors.releaseNumber = 'Release number is required';
    if (!formValues.minimumAcceptedVersion.trim()) {
      newErrors.minimumAcceptedVersion = 'Minimum accepted version is required';
    }
    if (!formValues.rolloutScope.trim()) {
      newErrors.rolloutScope = 'Rollout scope is required';
    }
    if (
      formValues.rolloutScope.trim() &&
      !['1', '2'].includes(formValues.rolloutScope.trim())
    ) {
      newErrors.rolloutScope = 'Rollout scope must be 1 or 2';
    }
    if (id === 'new' && !file) {
      newErrors.file =
        language?.['Upload version is required'] ?? 'Upload version is required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    webLoadingTrue();

    try {
      const formData = buildFormData();

      if (id === 'new') {
        await createAppVersion(formData);
        webLoadingFalse();
        webNavigate('/' + preLanguage + '/appVersion/');
        showToast({
          message: language?.['App version created'] ?? 'App version created',
          type: 'success',
          duration: 20000,
        });
      } else {
        await updateAppVersion(id, formData);
        webLoadingFalse();
        webNavigate('/' + preLanguage + '/appVersion/');
        showToast({
          message: language?.['App version updated'] ?? 'App version updated',
          type: 'success',
          duration: 20000,
        });
      }
    } catch (error: any) {
      webLoadingFalse();
      showApiError(error, {
        language,
        setFieldErrors: setErrors,
        titleFallback:
          id === 'new'
            ? language?.['Error creating app version'] ?? 'Error creating app version'
            : language?.['Error updating app version'] ?? 'Error updating app version',
      });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setPackageFile(f);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileName('');
    setErrors((prev: any) => {
      const next = { ...prev };
      delete next.file;
      return next;
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fieldWrap = (width: string) => ({
    width: isMobile ? '100%' : width,
  });

  const label = (text: string) => (
    <div className="flex items-center">
      <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>
        {text}
      </p>
      <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
        {language && language?.['*'] ? language?.['*'] : '*'}
      </p>
    </div>
  );

  return (
    <div className="bg-white h-full flex flex-col overflow-hidden">
      <div className="flex items-center cursor-pointer" onClick={() => webNavigate('/' + preLanguage + '/appVersion/')}>
        <div className="w-6 h-6 flex items-center justify-center">
          <BackIcon />
        </div>
        <p
          style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
          className="font-inter"
        >
          {language && language?.['Back'] ? language?.['Back'] : 'Back'}
        </p>
      </div>
      <p
        style={{
          margin: '20px 0px',
          fontSize: '20px',
          fontWeight: '500',
          lineHeight: '28px',
          color: '#222525',
        }}
        className="font-inter"
      >
        {id === 'new'
          ? language && language?.['Add app version']
            ? language?.['Add app version']
            : 'Add app version'
          : language && language?.['Edit app version']
            ? language?.['Edit app version']
            : 'Edit app version'}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col flex-1">
        <div
          className="scroll-container flex-1"
          style={{
            width: '100%',
            overflowY: 'auto',
            paddingBottom: '16px',
          }}
        >
          <div className="flex flex-wrap" style={{ gap: '24px' }}>
            <div className="space-y-2" style={fieldWrap('calc(33.333% - 16px)')}>
              {label(language?.['Platform'] ?? 'Platform')}
              <select
                value={formValues.platform}
                onChange={e => handlePlatformChange(e.target.value)}
                className="text-[14px] font-inter text-main border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none bg-transparent w-full"
              >
                <option value="" disabled>
                  {language?.['Select platform'] ?? 'Select platform'}
                </option>
                {PLATFORMS.map(p => (
                  <option key={p.value} value={p.value}>
                    {language?.[p.label] ?? p.label}
                  </option>
                ))}
              </select>
              {/* The app name is what the tills match on; show it, or the legacy one a release still carries. */}
              {(selectedPlatform || formValues.appName) && (
                <p className="text-[12px] font-inter" style={{ color: '#6A7878' }}>
                  {`${language?.['App name'] ?? 'App name'}: ${selectedPlatform?.appName ?? formValues.appName}`}
                </p>
              )}
              {(errors.platform || errors.appName) && (
                <p className="text-[12px] text-red-600 font-inter">{errors.platform ?? errors.appName}</p>
              )}
            </div>
            <div className="space-y-2" style={fieldWrap('calc(33.333% - 16px)')}>
              {label(language?.['Release name'] ?? 'Release name')}
              <input
                value={formValues.releaseName}
                {...limitProps(limits, 'releaseName')}
                onChange={e => handleChange('releaseName', e.target.value)}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none bg-transparent w-full"
              />
              <FieldCounter value={formValues.releaseName} max={limits.releaseName?.maxLength} />
              {errors.releaseName && (
                <p className="text-[12px] text-red-600 font-inter">{errors.releaseName}</p>
              )}
            </div>
            <div className="space-y-2" style={fieldWrap('calc(33.333% - 16px)')}>
              {label(language?.['Release number'] ?? 'Release number')}
              <input
                value={formValues.releaseNumber}
                {...limitProps(limits, 'releaseNumber')}
                onChange={e => handleChange('releaseNumber', e.target.value)}
                onKeyDown={validateMinAcceptedVersionKeyDown}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none bg-transparent w-full"
              />
              <FieldCounter value={formValues.releaseNumber} max={limits.releaseNumber?.maxLength} />
              {errors.releaseNumber && (
                <p className="text-[12px] text-red-600 font-inter">{errors.releaseNumber}</p>
              )}
            </div>
            <div className="space-y-2" style={fieldWrap('calc(50% - 12px)')}>
              {label(
                language?.['Minimum accepted version'] ?? 'Minimum accepted version'
              )}
              <input
                value={formValues.minimumAcceptedVersion}
                {...limitProps(limits, 'minimumAcceptedVersion')}
                onChange={e => {
                  const normalized = normalizeMinAcceptedVersion(e.target.value);
                  handleChange('minimumAcceptedVersion', normalized);
                }}
                onPaste={e => {
                  const text = e.clipboardData.getData('text') ?? '';
                  if (!isValidMinAcceptedVersionChars(text)) {
                    e.preventDefault();
                    return;
                  }
                  const normalized = normalizeMinAcceptedVersion(text);
                  e.preventDefault();
                  handleChange('minimumAcceptedVersion', normalized);
                }}
                onKeyDown={validateMinAcceptedVersionKeyDown}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none bg-transparent w-full"
              />
              <FieldCounter value={formValues.minimumAcceptedVersion} max={limits.minimumAcceptedVersion?.maxLength} />
              {errors.minimumAcceptedVersion && (
                <p className="text-[12px] text-red-600 font-inter">{errors.minimumAcceptedVersion}</p>
              )}
            </div>
            <div className="space-y-2" style={fieldWrap('calc(50% - 12px)')}>
              {label(language?.['Rollout scope'] ?? 'Rollout scope')}
              <select
                value={formValues.rolloutScope}
                onChange={e => handleChange('rolloutScope', e.target.value)}
                className="text-[14px] font-inter text-main border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none bg-transparent w-full"
              >
                <option value="" disabled>
                  {language?.['Select rollout scope'] ?? 'Select rollout scope'}
                </option>
                <option value="1">
                  {language?.['All devices'] ?? 'All devices'}
                </option>
                <option value="2">
                  {language?.['Selected devices only'] ?? 'Selected devices only'}
                </option>
              </select>
              {errors.rolloutScope && (
                <p className="text-[12px] text-red-600 font-inter">{errors.rolloutScope}</p>
              )}
            </div>
          </div>
          <div className="space-y-2" style={{ marginTop: '24px', width: '100%' }}>
            {label(language?.['Upload version'] ?? 'Upload version')}
            <input
              ref={fileInputRef}
              type="file"
              id="appVersionFileInput"
              accept={selectedPlatform?.accept ?? PLATFORMS.map(p => p.accept).join(',')}
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) setPackageFile(f);
              }}
            />
            <div
              className="w-full flex flex-col items-center justify-center cursor-pointer"
              style={{
                padding: '32px 20px',
                border: `1px dashed ${isDragging ? '#3AC0C4' : '#DBE4E4'}`,
                borderRadius: '16px',
                backgroundColor: '#FAFAFA',
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="space-y-2 text-center">
                <div className="flex items-center justify-center">
                  <UploadIcon />
                </div>
                <p className="text-[12px] font-inter mt-1" style={{ color: '#6A7878' }}>
                  {selectedPlatform
                    ? (language?.[`${selectedPlatform.extension} file`] ?? `${selectedPlatform.extension} file`)
                    : (language?.['.zip or .apk file'] ?? '.zip or .apk file')}
                </p>

                {fileName || (id !== 'new' && existingFileLabel && !fileName) ? (
                  <>
                    <p
                      style={{ fontSize: '14px', color: '#0F7679' }}
                      className="text-[14px] font-inter mt-2"
                    >
                      {fileName ? `File: ${fileName}` : `${language?.['Current file'] ?? 'Current file'}: ${existingFileLabel}`}
                    </p>
                    {fileName ? (
                      <p
                        className="text-[14px] font-inter font-semibold leading-[18px] cursor-pointer"
                        style={{ color: '#3AC0C4', textDecoration: 'underline', marginTop: '16px' }}
                        onClick={handleRemoveFile}
                      >
                        {language?.['Remove FIle'] ?? language?.['Remove file'] ?? 'Remove file'}
                      </p>
                    ) : (
                      id !== 'new' &&
                      existingPackageUrl && (
                        <p
                          className="text-[14px] font-inter font-semibold leading-[18px] cursor-pointer"
                          style={{ color: '#3AC0C4', textDecoration: 'underline', marginTop: '16px' }}
                          onClick={() => window.open(existingPackageUrl, '_blank')}
                        >
                          {language?.['View package'] ?? 'View package'}
                        </p>
                      )
                    )}
                  </>
                ) : (
                  <>
                    <p
                      style={{ fontSize: '16px', color: '#819596', marginBottom: '8px' }}
                      className="text-main leading-[20px] font-inter"
                    >
                      {language?.['OR'] ?? 'OR'}
                    </p>
                    <label
                      htmlFor="appVersionFileInput"
                      className="text-[14px] font-inter font-semibold leading-[18px] cursor-pointer"
                      style={{
                        color: '#0F7679',
                        padding: '8px 16px',
                        border: '1px solid #C9D7D8',
                        borderRadius: '30px',
                      }}
                    >
                      {language?.['Browse file'] ?? 'Browse file'}
                    </label>
                  </>
                )}
              </div>
            </div>
            {errors.file && (
              <p className="text-[12px] text-red-600 font-inter">{errors.file}</p>
            )}
          </div>
        </div>
        <div
          className="w-full flex items-center justify-end shadow_button relative mt-auto"
          style={{ gap: '12px', paddingTop: isMobile ? '16px' : '24px', paddingBottom: isMobile ? '16px' : '0px' }}
        >
          <div
            className="absolute"
            style={{
              left: '-24px',
              top: 0,
              height: '1px',
              backgroundColor: '#DBE4E4',
              width: isMobile ? 'calc(100vw + 32px)' : 'calc(-256px + 48px + 100vw)',
            }}
          />
          <button
            type="button"
            onClick={() => webNavigate('/' + preLanguage + '/appVersion/')}
            style={{
              padding: '10px 24px',
              borderRadius: '30px',
              color: '#222525',
              fontSize: '14px',
              fontWeight: '500',
              border: '1px solid #C9D7D8',
            }}
            className="font-inter cursor-pointer bg-white"
          >
            {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
          </button>
          <button
            type="submit"
            style={{
              padding: '10px 24px',
              borderRadius: '30px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: '#0F7679',
              border: 'none',
            }}
            className="font-inter cursor-pointer"
          >
            {language && language?.['Save'] ? language?.['Save'] : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAppVersion;
